const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/config/sro/eCheckin/sections/Versicherung.tsx';
import * as React from 'react';
import { forwardRef } from 'react';
import { useCIS4m, useCIS4mData } from '@cistec/common_web/modules/CIS4m/hooks';
import textMap from '../translations';
import CIS4mSingleCheckbox from '@cistec/common_web/modules/CIS4m/components/CIS4mSingleCheckbox';
import { ECheckinSectionProps } from 'App/components/eCheckin/types';
import ECheckinSection from 'App/components/eCheckin/components/ECheckinSection';
import BehandlungsgrundField from 'App/components/eCheckin/components/fields/metakat/BehandlungsgrundField';
import { VersicherungField } from 'App/components/eCheckin/components/fields/metakat/VersicherungField';
import { VersicherungsdeckungField } from 'App/components/eCheckin/components/fields/metakat/VersicherungsdeckungField';
import { createFieldPath } from 'App/components/eCheckin/utils';
import { useText } from '@cistec/common_web/utils/intlUtils';
import { CIS4mTextField } from '@cistec/common_web/modules/CIS4m/components/CIS4mTextField';
import { CIS4mBlock } from '@cistec/common_web/modules/CIS4m/components/CIS4mBlock';
import { YesNoField } from 'App/components/eCheckin/components/fields/YesNoField';
import { BirthDateField } from 'App/components/eCheckin/components/fields/BirthDateField';
import { AddressBlock, AddressFieldsConfig } from 'App/components/eCheckin/components/fields/AddressBlock';
import _ from 'lodash';
import { useIntl } from 'react-intl';
import { LabelLink } from 'App/components/eCheckin/components/fields/LabelLink';
import { Alert } from 'App/components/Alert/Alert';

const addressFieldsConfig: AddressFieldsConfig = {
    additionalInfo: {
        included: false,
    },
    country: {
        included: false,
    },
};

export const Versicherung = forwardRef((props: ECheckinSectionProps, ref: any) => {
    const { path } = props;
    const title = useText(props.title);
    const { formatMessage } = useIntl();

    const PATH_BEHANDLUNGSGRUND = createFieldPath(path, 'behandlungsgrund');
    const PATH_VERSICHERUNG_SEL = createFieldPath(path, 'versicherungSel');
    const PATH_INSURANCE = createFieldPath(path, 'versicherungName');
    const PATH_INSURANCE_ORT = createFieldPath(path, 'insuranceOrt');
    const PATH_INSURANCE_POLICY_NO = createFieldPath(path, 'insurancePolicyNo');
    const PATH_INSURANCE_CARD_NO = createFieldPath(path, 'insuranceCardNo');

    const PATH_ADDITIONAL_INSURANCE_QUESTION = createFieldPath(path, 'additionalInsuranceQuestion');
    const PATH_ADDITIONAL_INSURANCE_NAME = createFieldPath(path, 'additionalInsuranceName');
    const PATH_ADDITIONAL_INSURANCE_ORT = createFieldPath(path, 'additionalInsuranceOrt');
    const PATH_ADDITIONAL_INSURANCE_NO = createFieldPath(path, 'additionalInsuranceNo');

    const PATH_ACCIDENT_INSURANCE = createFieldPath(path, 'accidentInsurance');
    const PATH_ACCIDENT_INSURANCE_ORT = createFieldPath(path, 'accidentInsuranceOrt');
    const PATH_ACCIDENT_INSURANCE_NO = createFieldPath(path, 'accidentInsuranceNo');
    const PATH_ACCIDENT_DATE = createFieldPath(path, 'accidentDate');
    const PATH_COMPANY = createFieldPath(path, 'company');
    const PATH_PROFESSION = createFieldPath(path, 'profession');

    const PATH_CLASS = createFieldPath(path, 'klasse');
    const PATH_COMFORT_UPGRADE_1 = createFieldPath(path, 'comfortUpgrade1');
    const PATH_COMFORT_UPGRADE_2 = createFieldPath(path, 'comfortUpgrade2');
    const PATH_COMFORT_UPGRADE_3 = createFieldPath(path, 'comfortUpgrade3');
    const PATH_COMFORT_UPGRADE_4 = createFieldPath(path, 'comfortUpgrade4');

    const eCheckinSectionRef = useCIS4m(ref);
    const formData = useCIS4mData();

    const showAccidentFields = _.get(formData, PATH_BEHANDLUNGSGRUND) === 'accident';

    const showAdditionalFields = _.get(formData, PATH_ADDITIONAL_INSURANCE_QUESTION) === '1';

    const insuranceClass = _.get(formData, PATH_CLASS);
    const showComfortUpgradesAllgemein = insuranceClass === 'general';
    const showComfortUpgradeHalbprivat = insuranceClass === 'semi-private';
    const showUpgradeInfo = showComfortUpgradesAllgemein || showComfortUpgradeHalbprivat;

    return (
        <ECheckinSection ref={eCheckinSectionRef} path={path} title={title}>
            <h1>{title}</h1>

            <BehandlungsgrundField label={textMap.behandlungsgrund} path={PATH_BEHANDLUNGSGRUND} required />

            <VersicherungField label={textMap.zustVersicherung} path={PATH_VERSICHERUNG_SEL} required />

            <h4>{formatMessage(textMap.basicInsuranceTitle)}</h4>
            <CIS4mTextField label={textMap.versicherungName} path={PATH_INSURANCE} required />
            <CIS4mTextField label={textMap.insuranceOrt} path={PATH_INSURANCE_ORT} />
            <CIS4mTextField label={textMap.insurancePolicyNo} path={PATH_INSURANCE_POLICY_NO} required />
            <CIS4mTextField
                label={textMap.versichertenKartenNr}
                path={PATH_INSURANCE_CARD_NO}
                required
                mask={'80999999999999999999'}
            />

            <YesNoField
                label={textMap.additionalInsuranceQuestion}
                path={PATH_ADDITIONAL_INSURANCE_QUESTION}
                required
            />
            <CIS4mBlock hidden={!showAdditionalFields}>
                <CIS4mTextField
                    label={textMap.additionalInsuranceName}
                    path={PATH_ADDITIONAL_INSURANCE_NAME}
                    required
                />
                <CIS4mTextField label={textMap.additionalInsuranceOrt} path={PATH_ADDITIONAL_INSURANCE_ORT} />
                <CIS4mTextField label={textMap.additionalInsuranceNo} path={PATH_ADDITIONAL_INSURANCE_NO} required />
            </CIS4mBlock>

            <CIS4mBlock hidden={!showAccidentFields}>
                <Alert color={'info'} hideIcon>
                    {formatMessage(textMap.accidentInfoText)}
                </Alert>
                <CIS4mTextField label={textMap.accidentInsurance} path={PATH_ACCIDENT_INSURANCE} required />
                <CIS4mTextField label={textMap.accidentInsuranceOrt} path={PATH_ACCIDENT_INSURANCE_ORT} />
                <CIS4mTextField label={textMap.accidentInsuranceNo} path={PATH_ACCIDENT_INSURANCE_NO} required />
                <BirthDateField label={textMap.accidentDate} path={PATH_ACCIDENT_DATE} required />
                <h4>{formatMessage(textMap.employerTitle)}</h4>
                <CIS4mTextField label={textMap.company} path={PATH_COMPANY} required />
                <AddressBlock basePath={path} required fieldsConfig={addressFieldsConfig} />
                <CIS4mTextField label={textMap.profession} path={PATH_PROFESSION} required />
            </CIS4mBlock>

            <VersicherungsdeckungField label={textMap.klasse} path={PATH_CLASS} required />

            <CIS4mBlock hidden={!showUpgradeInfo}>
                <h4>{formatMessage(textMap.comfortUpgradesTitle)}</h4>
                <div>
                    {formatMessage(textMap.comfortUpgradesInfoLine1, {
                        link: (
                            <LabelLink
                                text={textMap.comfortUpgradesInfoLine1Link}
                                url="https://www.sro.ch/patienten-und-angehoerige/ihr-spitalaufenthalt/sro-plus-fuer-zusatzversicherte"
                            />
                        ),
                    })}
                </div>
                <p>{formatMessage(textMap.comfortUpgradesInfoLine2)}</p>
                <p>{formatMessage(textMap.comfortUpgradesInfoLine3)}</p>
            </CIS4mBlock>

            <CIS4mBlock hidden={!showComfortUpgradesAllgemein}>
                <CIS4mSingleCheckbox label={textMap.comfortUpgrade1} path={PATH_COMFORT_UPGRADE_1} />
                <CIS4mSingleCheckbox label={textMap.comfortUpgrade2} path={PATH_COMFORT_UPGRADE_2} />
                <CIS4mSingleCheckbox label={textMap.comfortUpgrade3} path={PATH_COMFORT_UPGRADE_3} />
            </CIS4mBlock>

            <CIS4mBlock hidden={!showComfortUpgradeHalbprivat}>
                <CIS4mSingleCheckbox label={textMap.comfortUpgrade4} path={PATH_COMFORT_UPGRADE_4} />
            </CIS4mBlock>
        </ECheckinSection>
    );
});

Versicherung.displayName = 'Versicherung';

export default Versicherung;
